import React from "react";
import { Eye, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Banner persistente que aparece SIEMPRE en la parte superior mientras el admin
 * esté impersonando a otro usuario. Rojo/amber para que no pase desapercibido +
 * botón grande de "Volver a mi cuenta". El backend ya bloquea cualquier mutación
 * (middleware `enforce_read_only_impersonation`) y el frontend muestra un toast
 * si se intenta desde el interceptor de axios.
 */
export function ImpersonationBanner() {
  const { impersonation, user, stopImpersonation } = useAuth();
  if (!impersonation?.active) return null;

  const handleExit = async () => {
    try {
      await stopImpersonation();
    } catch (e) {
      toast.error("No se pudo salir de la impersonación");
    }
  };

  return (
    <div
      className="sticky top-0 z-50 flex items-center gap-3 border-b border-amber-400/70 bg-amber-50 px-4 py-2 text-amber-900 shadow-sm dark:border-amber-500/50 dark:bg-amber-950/40 dark:text-amber-100"
      data-testid="impersonation-banner"
      role="status"
    >
      <Eye className="h-4 w-4 shrink-0" />
      <p className="flex-1 text-sm leading-tight">
        Estás viendo la app como{" "}
        <strong className="font-semibold">{user?.full_name}</strong>{" "}
        <span className="text-xs text-amber-800/80 dark:text-amber-200/70">
          ({user?.role_name}) · modo solo lectura
        </span>
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={handleExit}
        data-testid="impersonation-exit"
        className="h-8 border-amber-500 bg-white text-amber-900 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-100 dark:hover:bg-amber-900/50"
      >
        <LogOut className="mr-1.5 h-3.5 w-3.5" />
        Volver a mi cuenta
      </Button>
    </div>
  );
}
